import { CButton, CInput, CInputGroup, CInputGroupAppend } from '@coreui/react';
import * as React from 'react';
import { InputType } from 'reactstrap/es/Input';
import { IValue, KNumKeypad } from '../';
import { Utils } from '../../core/utils';
import { dispatcher } from '../../Karma';
import { InternalTypeEnum } from '../../models/InternalTypeEnum';

interface IInputModel {
	// 
	Key: string;						// Id du champ

	// Mise en forme / comportement
	InputInlineStyle: string;			// Style inline du champ
	Disable: boolean;					// True if field is disabled
	PlaceHolder?: string;				// Help text displayed if field is empty  (oui, on a baculé en anglais)

	// Pour les champs de type texte
	MinLength: number;					// Longueur mini de la zone de saisie
	MaxLength: number;					// Longueur max de la zone de saisie

	// Pour les champs numériques
	Min: number;						// Valeur minimle que peut prendre le champ
	Max: number;						// Valeur maximale du champ
	Step: number;						// Increment step if InputType==number, range

	// Edit options
	Value: string;						// Valeur du champ
	DataType: InternalTypeEnum;			// X3 Internal type
	InputType: InputType;				// HTML5 input type attribute
	Required: boolean;					// INdicates wether field is required or not
	HasFocus?: boolean;					// Indique que la zone a le focus au chargement
	PreviousValue: string;				// Valeur avant modification (pour CTRL Z)
	IsDirty: boolean;					// Indique que la valeur a été modifiée

	OnChange: string;					// Pour surcharge javascript de l'évènement onChange

	ErrorMessage: string;				// Message d'erreur à afficher sous le champ (en cas d'erreur de saisie, de format...)

	Pattern: string;
}


interface IProps {
	Control: IInputModel,
	bsSize?: "lg" | "sm",
	onChange?: (value: string) => void,
	onClick?: (event: any) => void,
}

interface IState {
	showKeypad: boolean;
	virtualKeyboard: boolean;
}

// Zone de saisie pour :
// - Les contrôles de type KControlInput
// - Les tableaux
//
export class KInput extends React.Component<IProps, IState> {

	inputRef: any;
	handleAutofocus: boolean = true;
	private separator: ":" | "." | "/" | " " = ".";

	public constructor(props: any) {
		super(props);

		this.state = {
			showKeypad: false,
			virtualKeyboard: dispatcher.isVirtualKeyboardActive,
		}

		this.inputRef = React.createRef();
		
		// Si la valeur est non définie, on la force à vide.
		// Sinon, le contrôle devient 'non contrôlé' par react et les maj ne seront pas suivies
		//  et on se prendra un warning tout moche en plus.
		if (this.props.Control.Value == undefined) this.props.Control.Value = "";

		// Permet de mettre à jour le mode du clavier virtuel en cas de changement
		dispatcher.onVirtualKeyboardChanged.add((state) => { this.setState({ virtualKeyboard: state }) });

	}

	//#region React Hacks

	componentDidMount = () => {
		this.props.Control.PreviousValue = this.props.Control.Value;		
	}

	componentDidUpdate = () => {
		if ((this.props.Control.HasFocus) && (this.handleAutofocus)) {
			if (this.inputRef.current) {
				this.inputRef.current.focus();
				this.handleAutofocus = false;
			}
		}
	}

	componentWillUnmount = () => {
		dispatcher.onVirtualKeyboardChanged.reset();
	}
	//#endregion

	//#region Events

	onClick = (event) => {
		if (this.props.onClick) this.props.onClick(event);
	}

	// Changement valeur 
	onChange = (event) => {
		if (this.props.Control.Disable) return;
		this.props.Control.Value = event.target.value;
		if (this.props.onChange) this.props.onChange(event.target.value);
	}

	// Le pavé numérique a perdu le focus, on va donc le masquer
	onNumKeypadBlur = () => {
		if (this.state.showKeypad) {
			this.setState({
				showKeypad: false,
			});
		}
		this.forceUpdate();
	}

	// Clic sur une touche de le pavé numérique
	onNumKeypadClick = (value: string) => {
		if (this.props.Control.Disable) return;
		this.props.Control.Value = value;
		if (this.props.onChange) this.props.onChange(value);
		this.forceUpdate();
	}

	//#endregion

	//#region Rendus

	renderNumKeypad = () => {
		// Contrôle désactivé => rien à rendre
		if (this.props.Control.Disable) return "";

		if (this.props.Control.InputType) {
			// Si on a spécifié le type via l'attribut inputtype = date, alors on ne s'occupe pas du pavé numérique
			if (" datetime date time number week".indexOf(" " + this.props.Control.InputType.toLowerCase()) != -1) {
				return "";
			}
		}

		switch (this.props.Control.DataType) {
			case InternalTypeEnum.Alpha:
			case InternalTypeEnum.Blob:
			case InternalTypeEnum.Clob:
			case InternalTypeEnum.Uuid:
			case InternalTypeEnum.Menu:
				return "";

			case InternalTypeEnum.Date:
			case InternalTypeEnum.Datetime:
			case InternalTypeEnum.Decimal:
			case InternalTypeEnum.Double:
			case InternalTypeEnum.Float:
			case InternalTypeEnum.Short:
			case InternalTypeEnum.Long:
			default:
				break;
		}

		return (
			<CInputGroupAppend>
				<KNumKeypad separator={this.separator} ctrl={this.props.Control as IValue} onMouseLeave={this.onNumKeypadBlur} onChange={this.onNumKeypadClick} visible={this.state.showKeypad}></KNumKeypad>
				<CButton size={this.props.bsSize} onClick={() => { this.setState({ showKeypad: true }); }}>⌨</CButton>
			</CInputGroupAppend>
		);
	}

	render() {
		let ctrl = this.props.Control;

		let numKeypad = this.renderNumKeypad();

		let inlineStyle = Utils.decodeInlineStyleFromJson(ctrl.InputInlineStyle);
		// 
		return (
			< CInputGroup >
				<CInput
					size={this.props.bsSize}
					name={ctrl.Key}
					value={ctrl.Value}
					style={inlineStyle}
					autoFocus={ctrl.HasFocus}
					innerRef={this.inputRef}
					min={ctrl.Min}
					max={ctrl.Max}
					minLength={ctrl.MinLength}
					maxLength={ctrl.MaxLength}
					placeholder={ctrl.PlaceHolder}
					readOnly={ctrl.Disable}
					required={ctrl.Required}
					step={ctrl.Step}
					type={ctrl.InputType as InputType}
					onChange={this.onChange}
					onClick={this.onClick}
					inputMode={!this.state.virtualKeyboard ? "none" : "text"}
					pattern={ctrl.Pattern}
				>
				</CInput>
				{numKeypad}
				</CInputGroup >
			
		)
	}

	//#endregion 
} 
